/**
 * /smartlinks/targeting — SmartLink targeting hub route.
 *
 * Declares a TanStack Router route that mounts `SmartlinkTargetingPage` with
 * the active SmartLink ID and initial tab read from URL search params:
 *
 *   /smartlinks/targeting?smartlinkId=sl_...&tab=geo
 *
 * Mirrors the shape used by `campaigns.os-targeting.tsx`: module-local route
 * factory keyed to `rootRoute`, plus a `buildRoute(parent)` hook for the root
 * router assembler — either works, the strict-ownership lane keeps us out of
 * `router.tsx`.
 */

import { createRoute, type AnyRoute } from "@tanstack/react-router";
import { rootRoute } from "./__root";
import {
	SmartlinkTargetingPage,
	type SmartlinkTargetingPageProps,
} from "@/features/smartlinks-targeting/SmartlinkTargetingPage";
import {
	isTargetingTabSlug,
	resolveTabFromSearch,
	type TargetingTabSlug,
} from "@/features/smartlinks-targeting/api";

// ---------------------------------------------------------------------------
// Search-param contract — operators deep-link to a (SmartLink, tab) pair.
// ---------------------------------------------------------------------------

export interface SmartlinkTargetingSearch {
	smartlinkId?: string;
	tab?: TargetingTabSlug;
}

function normalizeSearch(raw: Record<string, unknown>): SmartlinkTargetingSearch {
	const next: SmartlinkTargetingSearch = {};
	if (typeof raw.smartlinkId === "string" && raw.smartlinkId.trim().length > 0) {
		next.smartlinkId = raw.smartlinkId.trim();
	}
	const tabRaw = typeof raw.tab === "string" ? raw.tab : undefined;
	if (tabRaw && isTargetingTabSlug(tabRaw)) {
		next.tab = tabRaw;
	}
	return next;
}

// ---------------------------------------------------------------------------
// Route — mounts at `/smartlinks/targeting`. Keeps the shared router.tsx
// assembly out of this agent's lane; root wire-up appends a single
// `mk("/smartlinks/targeting", SmartlinkTargetingRoute as FC)` entry.
// ---------------------------------------------------------------------------

export const smartlinksTargetingRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/smartlinks/targeting",
	validateSearch: normalizeSearch,
	component: SmartlinkTargetingRoute,
});

export default smartlinksTargetingRoute;

/**
 * Factory for programmatic router assembly — equivalent pattern to
 * `campaigns.browser-targeting.tsx`'s `buildRoute(parent)`. Either this or
 * the module-level `smartlinksTargetingRoute` works when root wires up.
 */
export function buildSmartlinkTargetingRoute<TParent extends AnyRoute>(parent: TParent) {
	return createRoute({
		getParentRoute: () => parent,
		path: "/smartlinks/targeting",
		validateSearch: normalizeSearch,
		component: SmartlinkTargetingRoute,
	});
}

// ---------------------------------------------------------------------------
// Page shell — keeps the router-facing component thin. URL -> props mapping
// happens here so the feature component remains router-agnostic and
// unit-testable without TanStack Router mounted.
// ---------------------------------------------------------------------------

export function SmartlinkTargetingRoute() {
	// The strict ownership lane forbids touching `router.tsx`, so this route
	// is not yet in the TanStack Router registry. That rules out the typed
	// `route.useSearch()` helper (returns `never` until root wires the route).
	// We read URL search state directly via the browser — guarded for SSR /
	// non-browser test renders.
	const search: SmartlinkTargetingSearch =
		typeof window === "undefined"
			? {}
			: normalizeSearch(
					Object.fromEntries(new URLSearchParams(window.location.search)),
				);
	const initialTab = resolveTabFromSearch(search.tab);

	const handleTabChange: SmartlinkTargetingPageProps["onTabChange"] = (tab) => {
		if (typeof window === "undefined") return;
		const url = new URL(window.location.href);
		url.searchParams.set("tab", tab);
		window.history.replaceState(window.history.state, "", url.toString());
	};

	return (
		<SmartlinkTargetingPage
			initialSmartlinkId={search.smartlinkId}
			initialTab={initialTab}
			onTabChange={handleTabChange}
		/>
	);
}

// Convenience re-export so the root router can import either identifier.
export { SmartlinkTargetingRoute as SmartlinkTargetingPageRoute };
